import type React from 'react';
import { ComboBox, Input, ListBox } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { AccountOption } from '../../journalEntryMeta';

type AccountPickerProps = {
    ariaLabel: string;
    value: string | null;
    options: AccountOption[];
    placeholder: string;
    isDisabled?: boolean;
    onChange: (accountId: string | null) => void;
    /** testid discriminator (row key) appended after the literal slot. */
    slot: string;
};

/**
 * Searchable account picker — HeroUI's React-Aria ComboBox. Only postable
 * (leaf) accounts are passed in. Each option's `textValue` is `"code name"`, so
 * the ComboBox's built-in contains-filter matches typing either the code
 * (`1120`) or any part of the localized name (`bank` / `ბანკ`), in either
 * locale. Fully keyboard-operable (type-ahead, arrows, Enter to choose).
 *
 * `getNodeTestIdProp` (data-testid only, no `id`) is used deliberately — an
 * explicit `id` on the RAC ComboBox root triggers a setState loop.
 */
export const AccountPicker: React.FC<AccountPickerProps> = ({
    ariaLabel,
    value,
    options,
    placeholder,
    isDisabled,
    onChange,
    slot,
}) => (
    <ComboBox<AccountOption>
        aria-label={ariaLabel}
        defaultItems={options}
        isDisabled={isDisabled}
        menuTrigger="focus"
        onSelectionChange={(key) => onChange(key === null ? null : String(key))}
        selectedKey={value}
        {...getNodeTestIdProp(`journal-line-account-${slot}`)}
    >
        <ComboBox.InputGroup>
            <Input placeholder={placeholder} />
            <ComboBox.Trigger />
        </ComboBox.InputGroup>
        <ComboBox.Popover>
            <ListBox>
                {(item: AccountOption) => (
                    <ListBox.Item id={item.id} textValue={item.searchText}>
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="shrink-0 text-xs tabular-nums text-muted">
                                {item.code}
                            </span>
                            <span className="truncate">{item.name}</span>
                        </span>
                    </ListBox.Item>
                )}
            </ListBox>
        </ComboBox.Popover>
    </ComboBox>
);
